import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { usePagination } from './hooks/usePagination';
import { Text } from '../Text';
import { Colors } from 'basic-ui-lib/constants';
export interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
  maxVisiblePages?: number;
  suffixTotal?: ReactNode;
  labelDisplay?: string;
  showRowPerPage?: boolean;
  backgroundColorActivePage?: string;
}

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background-color: white;
  font-size: 0.875rem;
`;

const Display = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// const SelectionBox = styled.div`
//   gap: 8px;
//   display: flex;
//   align-items: center;
// `;

// const StyledSelect = styled.select`
//   padding: 0.6rem 0.75rem;
//   border: 1px solid ${Colors.neutral.border[3]};
//   border-radius: 4px;
//   font-weight: 500;
//   color: ${Colors.neutral.text[5]};
//   font-size: 14px;
//   background: transparent;
//   cursor: pointer;
//   outline: none;
//   transition: border-color 0.2s ease;
//   font-family: Be Vietnam Pro;

//   &:hover {
//     border-color: #d1d5db;
//   }

//   &:focus {
//     border-color: #6b7280;
//     box-shadow: 0 0 0 2px rgba(107, 114, 128, 0.2);
//   }
// `;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 2.5rem; // 40px
  padding: 0 1rem;

  border-radius: 4px;
  border: 1px solid #dadada;
  background: #ffffff;
  cursor: pointer;

  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    background-color: #f3f4f6;
  }
  &:disabled {
    opacity: 0.5;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const NumberButton = styled.button<{$backgroundColorActivePage?: string}>`
  display: flex;
  width: 40px;
  height: 40px;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: 1px solid #dadada;
  background: #ffffff;
  transition: all 0.2s ease;
  &.active {
    background-color: ${(props) => props.$backgroundColorActivePage || Colors.secondary[4]};
    border-color: ${(props) => props.$backgroundColorActivePage || Colors.secondary[4]};
  }
  &:hover:not(.active) {
    opacity: 0.8;
    background-color: #f3f4f6;
  }
  &:hover.active {
    opacity: 0.8;
  }

`;

const Ellipsis = styled.span`
  padding: 0.5rem;
  color: #6b7280;
`;

export const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  maxVisiblePages = 5,
  labelDisplay,
  showRowPerPage = false,
  backgroundColorActivePage,
}) => {
  const { pageNumbers, isFirstPage, isLastPage } = usePagination({
    totalItems,
    itemsPerPage,
    currentPage,
    maxVisiblePages,
  });

  const handlePrevious = () => {
    if (!isFirstPage) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (!isLastPage) {
      onPageChange(currentPage + 1);
    }
  };

  return (
    <Container>
      {showRowPerPage && (
        <Display>
          <Text body_1 medium color='neutral.text.6'>
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {labelDisplay}
          </Text>
        </Display>
      )}
      <Controls>
        {totalItems > 0 && (
          <>
            <ArrowButton onClick={handlePrevious} disabled={isFirstPage} aria-label='Previous page'>
              <Text body_1 medium color='neutral.text.3'>
                Previous
              </Text>
            </ArrowButton>

            {pageNumbers.map((pageNum, index) =>
              pageNum === '...' ? (
                <Ellipsis key={`ellipsis-${index}`}>...</Ellipsis>
              ) : (
                <NumberButton
                  className={pageNum === currentPage ? 'active' : ''}
                  key={pageNum}
                  onClick={() => typeof pageNum === 'number' && onPageChange(pageNum)}
                  aria-current={pageNum === currentPage ? 'page' : undefined}
                  $backgroundColorActivePage={backgroundColorActivePage}
                >
                  <Text body_1 medium color={pageNum === currentPage ? 'neutral.text.1' : 'neutral.text.3'}>
                    {pageNum}
                  </Text>
                </NumberButton>
              )
            )}

            <ArrowButton onClick={handleNext} disabled={isLastPage} aria-label='Next page'>
              <Text body_1 medium color='neutral.text.3'>
                Next
              </Text>
            </ArrowButton>
          </>
        )}
      </Controls>
    </Container>
  );
};

export default Pagination;
